import { useEffect } from 'react';
import { useActor } from '@xstate/react';
import { RadioGroup, Select, InfoBox } from '@crazyegginc/hatch';
import { CodeBox } from '/src/components/CodeBox';
import { generateEcommerceGoalScript } from '/src/features/goals/utils';

import { ECOMMERCE_LIST, ECOMMERCE_EVENTS_LIST } from '/src/features/goals/constants';

export function EcommerceTrigger({ service, triggerIndex }) {
  const [state] = useActor(service);
  const { send } = service;
  const values = state.context.payload.triggers[triggerIndex];
  const ecommerceEventList = ECOMMERCE_EVENTS_LIST[values.ecommerceApi.toUpperCase()];

  const RESTRICTED_ECOMMERCE = {
    [ECOMMERCE_LIST.SHOPIFY]: 'Shopify',
    [ECOMMERCE_LIST.MAGENTO]: 'Magento',
    [ECOMMERCE_LIST.SQUARESPACE]: 'Squarespace',
  };

  const restrictedEcommerceName = RESTRICTED_ECOMMERCE[values.ecommerceApi];

  useEffect(() => {
    if (!values.valueType) {
      send({ type: 'SET_TRIGGER', index: triggerIndex, key: 'valueType', value: 'CODE' });
    }
  }, [send, triggerIndex, values.valueType]);

  useEffect(() => {
    if (!ecommerceEventList) return;

    const currentEventOption = ecommerceEventList.find((option) => option.value === values.ecommerceEvent);

    const isRestricted = !currentEventOption || currentEventOption.disabled;

    if (isRestricted) {
      const defaultEvent = ecommerceEventList[0].value;

      if (values.ecommerceEvent !== defaultEvent) {
        send({
          type: 'SET_TRIGGER',
          index: triggerIndex,
          key: 'ecommerceEvent',
          value: defaultEvent,
        });
      }
    }
  }, [ecommerceEventList, values.ecommerceEvent, send, triggerIndex]);

  return (
    <>
      <RadioGroup
        label="Which ecommerce platform is your event for?"
        groupLabelClass="!text-xl !font-semibold !mb-5 !mt-10 !leading-none"
        options={[
          { value: ECOMMERCE_LIST.SHOPIFY, label: 'Shopify' },
          { value: ECOMMERCE_LIST.MAGENTO, label: 'Magento' },
          { value: ECOMMERCE_LIST.WOOCOMMERCE, label: 'WooCommerce' },
          { value: ECOMMERCE_LIST.WIXAPI, label: 'Wix' },
          { value: ECOMMERCE_LIST.SQUARESPACE, label: 'Squarespace' },
          { value: ECOMMERCE_LIST.GENERIC, label: 'Other' },
        ]}
        value={values.ecommerceApi}
        onChange={(ecommerceType) => {
          send({ type: 'SET_TRIGGER', index: triggerIndex, key: 'ecommerceApi', value: ecommerceType });

          if (RESTRICTED_ECOMMERCE[ecommerceType]) {
            const defaultEcommerceEvent = ECOMMERCE_EVENTS_LIST[ecommerceType.toUpperCase()][0].value;

            if (values.ecommerceEvent !== defaultEcommerceEvent) {
              send({
                type: 'SET_TRIGGER',
                index: triggerIndex,
                key: 'ecommerceEvent',
                value: defaultEcommerceEvent,
              });
            }
          }
        }}
      />

      {values.ecommerceApi ? (
        <>
          <div className="mt-10">
            <Select
              label="Which event triggers this goal?"
              labelClassName="!text-xl !font-semibold !mb-5 !leading-none"
              value={values.ecommerceEvent}
              onChange={(value) => {
                send({
                  type: 'SET_TRIGGER',
                  index: triggerIndex,
                  key: 'ecommerceEvent',
                  value,
                });
              }}
              options={ecommerceEventList}
            />
          </div>
          {restrictedEcommerceName && (
            <InfoBox
              className="!mt-2.5"
              body={
                <div className="leading-tight text-sm text-mako-500">
                  Due to restrictions from {restrictedEcommerceName}, no 3rd-party scripts can run on checkout pages. As
                  such, we cannot track checkout actions via Crazy Egg&apos;s tracking script. This restriction applies
                  to all 3rd-party JavaScript, not just Crazy Egg, but we are working on a solution.
                </div>
              }
            />
          )}
          {values.ecommerceApi === ECOMMERCE_LIST.GENERIC && (
            <div className="mb-2.5 mt-10">
              <h2 className="text-header-2 mb-5">Place this snippet where the event happens:</h2>
              <CodeBox
                loading={false}
                code={generateEcommerceGoalScript(values.ecommerceEvent)}
                canCopy={true}
                className="!pr-2.5"
              />

              <div className="text-body-2 my-10 ml-2 flex flex-col leading-6 text-black-pearl-500	">
                <div className="flex">
                  <strong className="w-6">1.</strong>
                  <span>Copy the code snippet</span>
                </div>
                <div className="flex">
                  <strong className="w-6">2.</strong>
                  <span>Place the code snippet on the page before the standard Crazy Egg tracking script.</span>
                </div>
              </div>

              <span className="text-body-2 text-lynch-500">
                <strong>Note:</strong> This is an advanced feature for developers. If you aren&#39;t sure how to
                implement this code snippet, we recommend using the other tracking options.
              </span>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
